import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Alert,
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Chip,
    Divider,
    FormControlLabel,
    Grid,
    InputAdornment,
    Paper,
    Stack,
    Switch,
    Tab,
    Tabs,
    TextField,
    Typography
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    Home as HomeIcon,
    PhoneIphone as MobilityIcon,
    RestartAlt as ResetIcon,
    Security as SecurityIcon,
    ShoppingCart as QuoteIcon,
    Tv as TvIcon,
    Wifi as InternetIcon
} from '@mui/icons-material';
import BundleBuilderTvRecommender from './BundleBuilderTvRecommender';
import { useAuth } from '../../contexts/AuthContext';
import {
    getInternetPabAdjustmentAmount,
    getInternetPricing,
    PAB_SURCHARGE
} from '../../features/bundle-builder/internetPricing';
import {
    getInternetTvPriceAdjustment,
    getTvDisplayPrice,
    getTvGiftAdjustment,
    getTvGiftOfferEligibility,
    getTvPriceBreakdown,
    TV_GIFT_OFFER
} from '../../features/bundle-builder/tvPricing';
import {
    formatMobilityLinePricingNote,
    getMobilityTotal
} from '../../features/bundle-builder/mobilityPricing';
import {
    formatMoney,
    formatSignedMoney
} from '../../features/bundle-builder/formatters';
import {
    formatPhoneForDisplay,
    getNormalizedPhoneDigits,
    isValidSendEmail,
    isValidSendName
} from '../../features/bundle-builder/sendValidation';
import {
    fillEmailPlaceholders
} from '../../features/bundle-builder/emailFormatting';
import ProductCard from '../../features/bundle-builder/components/ProductCard';
import BuilderCategoryTabs from '../../features/bundle-builder/components/BuilderCategoryTabs';
import BundleBuilderSendDialogs from '../../features/bundle-builder/components/BundleBuilderSendDialogs';
import QuickDiscountControls from '../../features/bundle-builder/components/QuickDiscountControls';
import QuoteSummary from '../../features/bundle-builder/components/QuoteSummary';
import { calculateBundleQuote } from '../../features/bundle-builder/quoteCalculator';
import { buildBundleBuilderEmailPreview } from '../../features/bundle-builder/emailRenderer';
import { buildCopyQuoteText } from '../../features/bundle-builder/quoteText';

import {
  BILL_CREDIT_AMOUNT,
  BUNDLE_BUILDER_SOURCE_LABEL,
  EXTRA_TV_BOX_PRICE,
  LOCKED_BUNDLE_BUILDER_SUBJECT,
  PVR_PRICE,
  internetPlans,
  longDistanceAddonIds,
  longDistanceAddons,
  orderedBySort,
  phonePlans,
  tvPackages,
} from '../../features/bundle-builder/catalogDefaults';
import {
  applyTvV2PackagePrices,
  buildTvRecommendationSelection,
  fetchTvV2PriceTable,
  getById,
  mapAddonsConfigGroups,
  mapCatalogConfigProducts,
  mapHomePhoneConfigProducts,
  mapInternetConfigProducts,
  mapTvDisplaySettings,
  normalizeEmailShellSettings,
} from '../../features/bundle-builder/catalogNormalization';
import {
  addonGroups,
  installNotes,
  mobilityCategories,
  mobilityPlans,
  seasonalNotes,
  securityPlans,
} from '../../features/bundle-builder/catalogDefaults';

const initialBuilderState = {
    internetId: 'internet-1-5g',
    tvId: '',
    tvRecommendation: null,
    phoneId: '',
    mobilityState: 'none',
    mobilityId: '',
    mobilityLines: 1,
    securityId: '',
    extraBoxes: 0,
    addPvr: false,
    tvGiftSelected: false,
    addonIds: [],
    addonQuantities: {},
    pabIncluded: true,
    eppEnabled: false,
    currentMonthlyBill: ''
};


function BundleBuilderPrototype({ agentBeta = false, agentViewMode = false }) {
    const { currentUser } = useAuth();
    const isAgentBeta = agentBeta;
    const canOpenAdminTools = isAgentBeta && Boolean(currentUser?.isAdmin) && !agentViewMode;
    const [activeTab, setActiveTab] = useState(0);
    const [mobilityCategory, setMobilityCategory] = useState('koodo');
    const [builder, setBuilder] = useState(initialBuilderState);
    const [copyStatus, setCopyStatus] = useState('');
    const [offerPreviewOpen, setOfferPreviewOpen] = useState(false);
    const [previewCopyStatus, setPreviewCopyStatus] = useState('');
    const [sendDryRunOpen, setSendDryRunOpen] = useState(false);
    const [sendDryRunLoading, setSendDryRunLoading] = useState(false);
    const [sendDryRunResult, setSendDryRunResult] = useState(null);
    const [sendConfirmOpen, setSendConfirmOpen] = useState(false);
    const [sendLoading, setSendLoading] = useState(false);
    const [sendResult, setSendResult] = useState(null);
    const [sendAttachments, setSendAttachments] = useState([]);
    const [sendValidationErrors, setSendValidationErrors] = useState({});
    const [bundleConfigVersion, setBundleConfigVersion] = useState(null);
    const [sendDryRunData, setSendDryRunData] = useState({
        customer_first_name: '',
        customer_full_name: '',
        customer_email: '',
        customer_phone: '',
        subject: LOCKED_BUNDLE_BUILDER_SUBJECT
    });
    const [internetConfigState, setInternetConfigState] = useState({
        source: 'fallback',
        plans: internetPlans
    });
    const [homePhoneConfigState, setHomePhoneConfigState] = useState({
        source: 'fallback',
        plans: phonePlans,
        longDistanceAddons
    });
    const [mobilityConfigState, setMobilityConfigState] = useState({
        source: 'fallback',
        categories: mobilityCategories,
        plans: mobilityPlans
    });
    const [securityConfigState, setSecurityConfigState] = useState({
        source: 'fallback',
        plans: securityPlans
    });
    const [addonsConfigState, setAddonsConfigState] = useState({
        source: 'fallback',
        groups: addonGroups
    });
    const [tvDisplayConfigState, setTvDisplayConfigState] = useState({
        source: 'fallback',
        packages: mapTvDisplaySettings([], tvPackages, { isAgentBeta })
    });
    const [emailShellConfigState, setEmailShellConfigState] = useState({
        source: 'fallback',
        shell: normalizeEmailShellSettings()
    });

    const mobilityDiscountActive = builder.mobilityState === 'existing' || builder.mobilityState === 'adding';
    const activeInternetPlans = internetConfigState.plans;
    const activePhonePlans = homePhoneConfigState.plans;
    const activeLongDistanceAddons = homePhoneConfigState.longDistanceAddons;
    const activeMobilityPlans = mobilityConfigState.plans;
    const activeMobilityCategories = mobilityCategories.filter((category) => (
        activeMobilityPlans.some((plan) => plan.category === category.id)
    ));
    const activeSecurityPlans = securityConfigState.plans;
    const activeAddonGroups = addonsConfigState.groups;
    const activeTvPackages = tvDisplayConfigState.packages;
    const activeEmailShell = emailShellConfigState.shell;
    const activeTvPackageByKey = useMemo(() => {
        const lookup = new Map();
        activeTvPackages.forEach((plan) => {
            if (plan.packageKey) {
                lookup.set(plan.packageKey, plan);
            }
        });
        return lookup;
    }, [activeTvPackages]);
    const activeAddonOptions = useMemo(
        () => activeAddonGroups.flatMap((group) => group.items.map((item) => ({ ...item, group: group.title }))),
        [activeAddonGroups]
    );
    const activeLongDistanceAddonIds = useMemo(
        () => activeLongDistanceAddons.map((addon) => addon.id),
        [activeLongDistanceAddons]
    );
    const selectedInternet = getById(activeInternetPlans, builder.internetId);
    const selectedTvRaw = builder.tvRecommendation || getById(activeTvPackages, builder.tvId);
    const selectedTv = useMemo(() => {
        if (!selectedTvRaw) {
            return null;
        }

        const metadataPlan = activeTvPackageByKey.get(selectedTvRaw.packageKey);
        if (!metadataPlan) {
            return selectedTvRaw;
        }

        const addOnCount = Number(selectedTvRaw.addOnCount || 0);
        const addOnTotal = Number(selectedTvRaw.addOnTotal || 0);
        const addOnLabel = addOnCount > 0
            ? ` + ${addOnCount} extra theme pack${addOnCount === 1 ? '' : 's'}`
            : '';
        const baseDisplayName = metadataPlan.name || selectedTvRaw.basePackageName || selectedTvRaw.name;

        return {
            ...selectedTvRaw,
            name: selectedTvRaw.source === 'tv-recommender' ? `${baseDisplayName}${addOnLabel}` : baseDisplayName,
            basePackageName: selectedTvRaw.source === 'tv-recommender' ? baseDisplayName : selectedTvRaw.basePackageName,
            pvrIncluded: metadataPlan.pvrIncluded,
            pvrMode: metadataPlan.pvrMode,
            pvrPrice: metadataPlan.pvrPrice,
            chips: metadataPlan.chips,
            cardDescription: metadataPlan.cardDescription,
            quoteSummaryNote: metadataPlan.quoteSummaryNote,
            internalAgentNote: metadataPlan.internalAgentNote,
            customerEmailEligible: metadataPlan.customerEmailEligible,
            emailDisplayName: selectedTvRaw.source === 'tv-recommender' ? `${metadataPlan.emailDisplayName || baseDisplayName}${addOnLabel}` : metadataPlan.emailDisplayName,
            emailBullets: metadataPlan.emailBullets,
            emailIncludeInQuoteTable: metadataPlan.emailIncludeInQuoteTable,
            regularPrice: selectedTvRaw.source === 'tv-recommender'
                ? Number(metadataPlan.regularPrice ?? selectedTvRaw.regularPrice ?? selectedTvRaw.price) + addOnTotal
                : metadataPlan.regularPrice ?? selectedTvRaw.regularPrice
        };
    }, [activeTvPackageByKey, selectedTvRaw]);
    const selectedPhone = getById(activePhonePlans, builder.phoneId);
    const selectedMobility = getById(activeMobilityPlans, builder.mobilityId);
    const selectedSecurity = getById(activeSecurityPlans, builder.securityId);
    const selectedAddons = activeAddonOptions
        .filter((addon) => builder.addonIds.includes(addon.id))
        .map((addon) => ({
            ...addon,
            quantity: addon.allowQuantity ? Math.max(1, Number(builder.addonQuantities[addon.id]) || 1) : 1
        }));
    const selectedLongDistance = activeLongDistanceAddons.filter((addon) => builder.addonIds.includes(addon.id));
    const activeTvPriceAdjustment = useMemo(
        () => getInternetTvPriceAdjustment(selectedInternet),
        [selectedInternet]
    );
    const tvGiftOfferEligibility = useMemo(
        () => getTvGiftOfferEligibility(selectedInternet, selectedTv),
        [selectedInternet, selectedTv]
    );
    const activeTvGiftAdjustment = useMemo(
        () => getTvGiftAdjustment(selectedInternet, selectedTv, builder.tvGiftSelected),
        [builder.tvGiftSelected, selectedInternet, selectedTv]
    );
    const filteredMobilityPlans = orderedBySort(activeMobilityPlans.filter((plan) => plan.category === mobilityCategory));
    const bundleBuilderSendSummary = useMemo(() => {
        const internetPricing = selectedInternet
            ? getInternetPricing(selectedInternet, mobilityDiscountActive, builder.eppEnabled, builder.pabIncluded)
            : null;
        const tvBreakdown = selectedTv
            ? getTvPriceBreakdown(selectedTv, builder.eppEnabled, activeTvPriceAdjustment, activeTvGiftAdjustment)
            : null;
        const selectedProducts = [
            selectedInternet
                ? { category: 'internet', name: selectedInternet.name, price: internetPricing?.belowLine || 0 }
                : null,
            selectedTv
                ? {
                    category: 'tv',
                    name: selectedTv.name,
                    price: tvBreakdown?.finalTotal || 0,
                    source: selectedTv.source || 'manual',
                    addOnCount: selectedTv.addOnCount || 0
                }
                : null,
            selectedPhone
                ? { category: 'home_phone', name: selectedPhone.name, price: selectedPhone.price }
                : null
        ].filter(Boolean);
        const includedProducts = selectedProducts.map((product) => `${product.category === 'home_phone' ? 'Home Phone' : product.category === 'tv' ? 'TV' : 'Internet'}: ${product.name}`);
        const unsupportedProducts = [
            builder.mobilityState === 'adding' && selectedMobility
                ? { type: 'Mobility plan', name: selectedMobility.name }
                : null,
            selectedSecurity
                ? { type: 'Security', name: selectedSecurity.name }
                : null,
            ...selectedAddons.map((addon) => ({ type: 'Add-on', name: addon.name })),
            ...selectedLongDistance.map((addon) => ({ type: 'Add-on', name: addon.name }))
        ].filter(Boolean);

        return {
            sourceLabel: BUNDLE_BUILDER_SOURCE_LABEL,
            includedProducts,
            selectedProducts,
            unsupportedProducts
        };
    }, [activeTvGiftAdjustment, activeTvPriceAdjustment, builder.eppEnabled, builder.mobilityState, builder.pabIncluded, mobilityDiscountActive, selectedAddons, selectedInternet, selectedLongDistance, selectedMobility, selectedPhone, selectedSecurity, selectedTv]);

    useEffect(() => {
        let cancelled = false;

        const loadActiveProductConfig = async () => {
            try {
                const [response, tvV2PriceTable] = await Promise.all([
                    fetch('/api/bundle-builder/config'),
                    fetchTvV2PriceTable()
                ]);
                if (!response.ok) {
                    throw new Error(`Config request failed with status ${response.status}`);
                }

                const data = await response.json();
                const activeConfig = data.config || data;
                const products = activeConfig?.products || {};
                const configuredEmailShell = activeConfig?.emailShell;
                const activeVersion = activeConfig?.meta?.storageStatus?.activeVersion
                    || activeConfig?.meta?.storage?.versionNumber
                    || data.storage?.active?.versionNumber
                    || null;
                const configInternet = products.internet;
                const homePhone = products.homePhone;
                const mobility = products.mobility;
                const security = products.security;
                const addons = products.addons;
                const tvDisplaySettings = activeConfig?.tvDisplaySettings;
                const builderVisibilityOptions = { isAgentBeta };
                const mappedInternetPlans = Array.isArray(configInternet)
                    ? mapInternetConfigProducts(configInternet, internetPlans, builderVisibilityOptions)
                    : [];
                const configPlans = homePhone?.plans;
                const configAddons = homePhone?.longDistanceAddons;
                const mappedPlans = Array.isArray(configPlans)
                    ? mapHomePhoneConfigProducts(configPlans, phonePlans, builderVisibilityOptions)
                    : [];
                const mappedAddons = Array.isArray(configAddons)
                    ? mapHomePhoneConfigProducts(configAddons, longDistanceAddons, builderVisibilityOptions)
                    : [];
                const mappedMobilityPlans = Array.isArray(mobility?.products)
                    ? mapCatalogConfigProducts(mobility.products, mobilityPlans, 'mobility', builderVisibilityOptions)
                    : [];
                const mappedSecurityPlans = Array.isArray(security?.products)
                    ? mapCatalogConfigProducts(security.products, securityPlans, 'security', builderVisibilityOptions)
                    : [];
                const mappedAddonGroups = Array.isArray(addons?.groups)
                    ? mapAddonsConfigGroups(addons.groups, addonGroups, builderVisibilityOptions)
                    : [];
                const hasTvDisplaySettings = Array.isArray(tvDisplaySettings?.packages);
                const mappedTvPackages = hasTvDisplaySettings
                    ? mapTvDisplaySettings(tvDisplaySettings.packages, tvPackages, builderVisibilityOptions)
                    : [];
                const pricedTvPackages = applyTvV2PackagePrices(
                    hasTvDisplaySettings ? mappedTvPackages : mapTvDisplaySettings([], tvPackages, builderVisibilityOptions),
                    tvV2PriceTable
                );

                if (!cancelled) {
                    setInternetConfigState({
                        source: mappedInternetPlans.length > 0 ? 'active' : 'fallback',
                        plans: mappedInternetPlans.length > 0 ? mappedInternetPlans : internetPlans
                    });
                    setHomePhoneConfigState({
                        source: mappedPlans.length > 0 || mappedAddons.length > 0 ? 'active' : 'fallback',
                        plans: mappedPlans.length > 0 ? mappedPlans : phonePlans,
                        longDistanceAddons: mappedAddons.length > 0 ? mappedAddons : longDistanceAddons
                    });
                    setMobilityConfigState({
                        source: mappedMobilityPlans.length > 0 ? 'active' : 'fallback',
                        categories: Array.isArray(mobility?.categories) && mobility.categories.length > 0 ? mobility.categories : mobilityCategories,
                        plans: mappedMobilityPlans.length > 0 ? mappedMobilityPlans : mobilityPlans
                    });
                    setSecurityConfigState({
                        source: mappedSecurityPlans.length > 0 ? 'active' : 'fallback',
                        plans: mappedSecurityPlans.length > 0 ? mappedSecurityPlans : securityPlans
                    });
                    setAddonsConfigState({
                        source: mappedAddonGroups.length > 0 ? 'active' : 'fallback',
                        groups: mappedAddonGroups.length > 0 ? mappedAddonGroups : addonGroups
                    });
                    setTvDisplayConfigState({
                        source: hasTvDisplaySettings ? 'active' : 'fallback',
                        packages: pricedTvPackages
                    });
                    setEmailShellConfigState({
                        source: configuredEmailShell ? 'active' : 'fallback',
                        shell: normalizeEmailShellSettings(configuredEmailShell || {})
                    });
                    setBundleConfigVersion(activeVersion);
                }
            } catch (error) {
                if (!cancelled) {
                    setInternetConfigState({
                        source: 'fallback',
                        plans: internetPlans
                    });
                    setHomePhoneConfigState({
                        source: 'fallback',
                        plans: phonePlans,
                        longDistanceAddons
                    });
                    setMobilityConfigState({
                        source: 'fallback',
                        categories: mobilityCategories,
                        plans: mobilityPlans
                    });
                    setSecurityConfigState({
                        source: 'fallback',
                        plans: securityPlans
                    });
                    setAddonsConfigState({
                        source: 'fallback',
                        groups: addonGroups
                    });
                    setTvDisplayConfigState({
                        source: 'fallback',
                        packages: mapTvDisplaySettings([], tvPackages, { isAgentBeta })
                    });
                    setEmailShellConfigState({
                        source: 'fallback',
                        shell: normalizeEmailShellSettings()
                    });
                    setBundleConfigVersion(null);
                }
            }
        };

        loadActiveProductConfig();

        return () => {
            cancelled = true;
        };
    }, [isAgentBeta]);

    useEffect(() => {
        setBuilder((current) => {
            const selectedActivePlan = activePhonePlans.find((plan) => plan.id === current.phoneId);
            const phoneStillSelectable = !current.phoneId || (selectedActivePlan && selectedActivePlan.quoteEligible !== false);
            const validAddonIds = new Set(
                activeLongDistanceAddons
                    .filter((addon) => addon.quoteEligible !== false)
                    .map((addon) => addon.id)
            );
            const nextAddonIds = current.addonIds.filter((addonId) => (
                !longDistanceAddonIds.includes(addonId) || validAddonIds.has(addonId)
            ));

            if (phoneStillSelectable && nextAddonIds.length === current.addonIds.length) {
                return current;
            }

            return {
                ...current,
                phoneId: phoneStillSelectable ? current.phoneId : '',
                addonIds: phoneStillSelectable ? nextAddonIds : nextAddonIds.filter((addonId) => !validAddonIds.has(addonId))
            };
        });
    }, [activePhonePlans, activeLongDistanceAddons]);

    useEffect(() => {
        setBuilder((current) => {
            const selectedActivePlan = activeInternetPlans.find((plan) => plan.id === current.internetId);
            const internetStillSelectable = !current.internetId || (selectedActivePlan && selectedActivePlan.quoteEligible !== false);

            if (internetStillSelectable) {
                return current;
            }

            return {
                ...current,
                internetId: ''
            };
        });
    }, [activeInternetPlans]);

    useEffect(() => {
        setBuilder((current) => {
            if (current.tvRecommendation) {
                return current;
            }

            const selectedActiveTv = activeTvPackages.find((plan) => plan.id === current.tvId);
            const tvStillSelectable = !current.tvId || Boolean(selectedActiveTv);
            const pvrStillAvailable = selectedActiveTv?.pvrMode === 'optional';

            if (tvStillSelectable && (pvrStillAvailable || !current.addPvr)) {
                return current;
            }

            return {
                ...current,
                tvId: tvStillSelectable ? current.tvId : '',
                addPvr: tvStillSelectable ? current.addPvr && pvrStillAvailable : false,
                extraBoxes: tvStillSelectable ? current.extraBoxes : 0
            };
        });
    }, [activeTvPackages]);

    useEffect(() => {
        if (tvGiftOfferEligibility.eligible) {
            return;
        }

        setBuilder((current) => (
            current.tvGiftSelected
                ? { ...current, tvGiftSelected: false }
                : current
        ));
    }, [tvGiftOfferEligibility.eligible]);

    useEffect(() => {
        const categoryStillExists = activeMobilityCategories.some((category) => category.id === mobilityCategory);

        if (!categoryStillExists && activeMobilityCategories.length > 0) {
            setMobilityCategory(activeMobilityCategories[0].id);
        }
    }, [activeMobilityCategories, mobilityCategory]);

    useEffect(() => {
        setBuilder((current) => {
            const selectedActiveMobility = activeMobilityPlans.find((plan) => plan.id === current.mobilityId);
            const selectedActiveSecurity = activeSecurityPlans.find((plan) => plan.id === current.securityId);
            const validAddonIds = new Set(
                activeAddonOptions
                    .filter((addon) => addon.quoteEligible !== false)
                    .map((addon) => addon.id)
            );
            const nextAddonIds = current.addonIds.filter((addonId) => (
                activeLongDistanceAddonIds.includes(addonId) || validAddonIds.has(addonId)
            ));
            const mobilityStillSelectable = !current.mobilityId || (selectedActiveMobility && selectedActiveMobility.quoteEligible !== false);
            const securityStillSelectable = !current.securityId || (selectedActiveSecurity && selectedActiveSecurity.quoteEligible !== false);

            if (
                mobilityStillSelectable
                && securityStillSelectable
                && nextAddonIds.length === current.addonIds.length
            ) {
                return current;
            }

            return {
                ...current,
                mobilityId: mobilityStillSelectable ? current.mobilityId : '',
                securityId: securityStillSelectable ? current.securityId : '',
                addonIds: nextAddonIds
            };
        });
    }, [activeMobilityPlans, activeSecurityPlans, activeAddonOptions, activeLongDistanceAddonIds]);

    const quote = useMemo(() => calculateBundleQuote({
        builder,
        mobilityDiscountActive,
        selectedAddons,
        activeTvGiftAdjustment,
        activeTvPriceAdjustment,
        selectedInternet,
        selectedLongDistance,
        selectedMobility,
        selectedPhone,
        selectedSecurity,
        selectedTv,
        extraTvBoxPrice: EXTRA_TV_BOX_PRICE,
        pvrPrice: PVR_PRICE,
        billCreditAmount: BILL_CREDIT_AMOUNT
    }), [
        builder,
        mobilityDiscountActive,
        selectedAddons,
        activeTvGiftAdjustment,
        activeTvPriceAdjustment,
        selectedInternet,
        selectedLongDistance,
        selectedMobility,
        selectedPhone,
        selectedSecurity,
        selectedTv
    ]);

    const offerEmailPreview = useMemo(() => buildBundleBuilderEmailPreview({
        activeEmailShell,
        activeTvGiftAdjustment,
        activeTvPriceAdjustment,
        builder,
        mobilityDiscountActive,
        selectedAddons,
        selectedInternet,
        selectedLongDistance,
        selectedMobility,
        selectedPhone,
        selectedSecurity,
        selectedTv,
        billCreditAmount: BILL_CREDIT_AMOUNT
    }), [
        activeEmailShell,
        activeTvGiftAdjustment,
        activeTvPriceAdjustment,
        builder,
        mobilityDiscountActive,
        selectedAddons,
        selectedInternet,
        selectedLongDistance,
        selectedMobility,
        selectedPhone,
        selectedSecurity,
        selectedTv
    ]);
    const updateBuilder = (updates) => {
        setBuilder((current) => ({ ...current, ...updates }));
        setCopyStatus('');
    };

    const toggleAddon = (addonId) => {
        setBuilder((current) => ({
            ...current,
            addonIds: current.addonIds.includes(addonId)
                ? current.addonIds.filter((id) => id !== addonId)
                : [...current.addonIds, addonId],
            addonQuantities: current.addonIds.includes(addonId)
                ? Object.fromEntries(Object.entries(current.addonQuantities).filter(([id]) => id !== addonId))
                : current.addonQuantities
        }));
        setCopyStatus('');
    };

    const updateAddonQuantity = (addonId, quantity) => {
        setBuilder((current) => ({
            ...current,
            addonQuantities: {
                ...current.addonQuantities,
                [addonId]: Math.max(1, Number(quantity) || 1)
            }
        }));
        setCopyStatus('');
    };

    const handleMobilityQuickToggle = (checked) => {
        if (isAgentBeta) {
            updateBuilder(checked ? { mobilityState: 'existing', mobilityId: '' } : { mobilityState: 'none', mobilityId: '' });
            return;
        }

        updateBuilder(checked
            ? { mobilityState: builder.mobilityState === 'adding' ? 'adding' : 'existing' }
            : { mobilityState: 'none', mobilityId: '' }
        );
    };

    const handleUseTvRecommendation = (recommendation, context) => {
        const tvSelection = buildTvRecommendationSelection(recommendation, context);

        if (!tvSelection) {
            return;
        }

        updateBuilder({
            tvId: tvSelection.manualTvId || tvSelection.id,
            tvRecommendation: tvSelection,
            addPvr: false
        });
    };

    const renderDiscountToggleBar = () => {
        const selectedInternetPabAdjustment = selectedInternet ? getInternetPabAdjustmentAmount(selectedInternet) : PAB_SURCHARGE;
        const quickToggles = [
            {
                id: 'pab',
                title: 'PAB Auto Pay Discount',
                checked: builder.pabIncluded,
                onChange: (checked) => updateBuilder({ pabIncluded: checked }),
                activeLabel: 'PAB included',
                inactiveLabel: `PAB off: +${formatMoney(selectedInternetPabAdjustment)}`,
                helper: 'Pre-auth bank payment discount'
            },
            {
                id: 'mobility',
                title: 'Current TELUS/Koodo mobility discount',
                checked: mobilityDiscountActive,
                onChange: handleMobilityQuickToggle,
                activeLabel: builder.mobilityState === 'adding' ? 'Active from new line' : 'Existing mobility active',
                inactiveLabel: 'No mobility discount',
                helper: 'Shortcut for Internet mobility discount'
            },
            {
                id: 'epp',
                title: 'EPP Closing Tool',
                checked: builder.eppEnabled,
                onChange: (checked) => updateBuilder({ eppEnabled: checked }),
                activeLabel: 'EPP discount on',
                inactiveLabel: 'EPP off',
                helper: isAgentBeta ? 'Applies current EPP discount logic' : 'Applies current draft EPP logic'
            }
        ];

        return <QuickDiscountControls billCredit={quote.billCredit} toggles={quickToggles} />;
    };

    const copyQuote = async () => {
        try {
            await navigator.clipboard.writeText(buildCopyQuoteText({
                builder,
                mobilityDiscountActive,
                quote,
                selectedTv
            }));
            setCopyStatus('Quote copied to clipboard.');
        } catch (error) {
            setCopyStatus('Copy failed. Select and copy the quote summary manually.');
        }
    };

    const copyOfferEmailHtml = async () => {
        if (!offerEmailPreview.hasHomeServices) {
            setPreviewCopyStatus('No approved email preview is available for this selection yet.');
            return;
        }

        try {
            await navigator.clipboard.writeText(offerEmailPreview.html);
            setPreviewCopyStatus('Email HTML copied to clipboard.');
        } catch (error) {
            setPreviewCopyStatus('Copy failed. Select and copy the preview manually.');
        }
    };

    const copyOfferEmailText = async () => {
        if (!offerEmailPreview.hasHomeServices) {
            setPreviewCopyStatus('No approved email preview is available for this selection yet.');
            return;
        }

        try {
            await navigator.clipboard.writeText(offerEmailPreview.plainText);
            setPreviewCopyStatus('Plain text summary copied to clipboard.');
        } catch (error) {
            setPreviewCopyStatus('Copy failed. Select and copy the summary manually.');
        }
    };

    const getSendCustomerValidationErrors = () => {
        const errors = {};
        const customerFirstName = sendDryRunData.customer_first_name.trim();
        const customerFullName = sendDryRunData.customer_full_name.trim();
        const customerEmail = sendDryRunData.customer_email.trim();
        const customerPhoneDigits = getNormalizedPhoneDigits(sendDryRunData.customer_phone);
        const agentPhoneDigits = getNormalizedPhoneDigits(currentUser?.phone);

        if (!customerFirstName) {
            errors.customer_first_name = 'Customer first name is required.';
        } else if (!isValidSendName(customerFirstName)) {
            errors.customer_first_name = 'Use letters, spaces, hyphens, and apostrophes only.';
        }

        if (!customerFullName) {
            errors.customer_full_name = 'Customer full name is required.';
        } else if (!isValidSendName(customerFullName)) {
            errors.customer_full_name = 'Use letters, spaces, hyphens, and apostrophes only.';
        }

        if (!customerEmail) {
            errors.customer_email = 'Customer email is required.';
        } else if (!isValidSendEmail(customerEmail)) {
            errors.customer_email = 'Enter a valid email address.';
        }

        if (!customerPhoneDigits) {
            errors.customer_phone = 'Customer phone is required.';
        } else if (customerPhoneDigits.length !== 10) {
            errors.customer_phone = 'Enter a 10-digit phone number.';
        }

        if (!currentUser?.name) {
            errors.agent_name = 'Agent name is missing from the logged-in profile.';
        }

        if (!agentPhoneDigits || agentPhoneDigits.length !== 10) {
            errors.agent_phone = 'Agent phone must be a 10-digit number in the user profile.';
        }

        return errors;
    };

    const resetSendValidation = () => {
        setSendDryRunResult(null);
        setSendResult(null);
        setSendValidationErrors({});
    };

    const getSendPlaceholderValues = () => ({
        customer_first_name: sendDryRunData.customer_first_name.trim(),
        customer_full_name: sendDryRunData.customer_full_name.trim(),
        customer_email: sendDryRunData.customer_email.trim(),
        customer_phone: formatPhoneForDisplay(sendDryRunData.customer_phone),
        agent_name: currentUser?.name || '',
        agent_phone: formatPhoneForDisplay(currentUser?.phone || '')
    });

    const finalEmailPreviewHtml = fillEmailPlaceholders(offerEmailPreview.html, getSendPlaceholderValues());
    const canShowFinalSendPreview = !isAgentBeta || sendDryRunResult?.success;

    const handleBundleAttachmentUpload = (event) => {
        const files = Array.from(event.target.files || []);
        const nextFiles = [...sendAttachments, ...files];
        const totalSize = nextFiles.reduce((sum, file) => sum + file.size, 0);

        if (nextFiles.length > 5) {
            setSendDryRunResult({
                success: false,
                errors: ['Maximum 5 files allowed per email.'],
                warnings: [],
                proposedSubject: LOCKED_BUNDLE_BUILDER_SUBJECT,
                proposedSourceLabel: BUNDLE_BUILDER_SOURCE_LABEL
            });
            event.target.value = '';
            return;
        }

        if (totalSize > 25 * 1024 * 1024) {
            setSendDryRunResult({
                success: false,
                errors: ['Total attachment size cannot exceed 25MB.'],
                warnings: [],
                proposedSubject: LOCKED_BUNDLE_BUILDER_SUBJECT,
                proposedSourceLabel: BUNDLE_BUILDER_SOURCE_LABEL
            });
            event.target.value = '';
            return;
        }

        setSendAttachments(nextFiles);
        setSendDryRunResult(null);
        setSendResult(null);
        event.target.value = '';
    };

    const removeBundleAttachment = (index) => {
        setSendAttachments((current) => current.filter((_, fileIndex) => fileIndex !== index));
        setSendDryRunResult(null);
        setSendResult(null);
    };

    const handlePrepareSendDryRun = async () => {
        const customerErrors = getSendCustomerValidationErrors();

        if (Object.keys(customerErrors).length > 0) {
            setSendValidationErrors(customerErrors);
            setSendDryRunResult({
                success: false,
                errors: Object.values(customerErrors),
                warnings: [],
                proposedSubject: LOCKED_BUNDLE_BUILDER_SUBJECT,
                proposedSourceLabel: BUNDLE_BUILDER_SOURCE_LABEL
            });
            setSendResult(null);
            return;
        }

        setSendValidationErrors({});
        const placeholderValues = getSendPlaceholderValues();

        try {
            setSendDryRunLoading(true);
            setSendDryRunResult(null);
            setSendResult(null);
            const response = await fetch('/api/bundle-builder/send-offer/validate', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    customerData: placeholderValues,
                    placeholderValues,
                    subject: LOCKED_BUNDLE_BUILDER_SUBJECT,
                    htmlContent: offerEmailPreview.html,
                    selectedProducts: bundleBuilderSendSummary.selectedProducts,
                    unsupportedProducts: bundleBuilderSendSummary.unsupportedProducts
                })
            });
            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.error || 'Send validation failed.');
            }

            setSendDryRunResult(result);
        } catch (error) {
            setSendDryRunResult({
                success: false,
                errors: [error.message || 'Send validation failed.'],
                warnings: [],
                proposedSubject: LOCKED_BUNDLE_BUILDER_SUBJECT,
                proposedSourceLabel: BUNDLE_BUILDER_SOURCE_LABEL
            });
        } finally {
            setSendDryRunLoading(false);
        }
    };

    const handleSendBundleOffer = async () => {
        const placeholderValues = getSendPlaceholderValues();
        const formData = new FormData();

        formData.append('customerData', JSON.stringify(placeholderValues));
        formData.append('subject', LOCKED_BUNDLE_BUILDER_SUBJECT);
        formData.append('htmlContent', offerEmailPreview.html);
        formData.append('validationToken', sendDryRunResult?.validationToken || '');
        formData.append('quoteSummary', JSON.stringify({
            monthlyTotal: quote.monthlySubtotal,
            billCredit: quote.billCredit
        }));
        formData.append('sourceMetadata', JSON.stringify({
            selectedProducts: bundleBuilderSendSummary.selectedProducts,
            unsupportedProducts: bundleBuilderSendSummary.unsupportedProducts,
            monthlyTotal: quote.monthlySubtotal,
            billCredit: quote.billCredit,
            configVersion: bundleConfigVersion,
            emailShellVersion: bundleConfigVersion
        }));
        sendAttachments.forEach((file) => {
            formData.append('attachments', file);
        });

        try {
            setSendLoading(true);
            setSendResult(null);
            const response = await fetch('/api/bundle-builder/send-offer', {
                method: 'POST',
                body: formData
            });
            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.errors?.join(' ') || result.error || 'Failed to send Bundle Builder quote.');
            }

            setSendConfirmOpen(false);
            setSendDryRunResult(null);
            setSendResult({
                success: true,
                message: result.message || 'Bundle Builder quote sent successfully.',
                sentOfferId: result.sentOfferId
            });
        } catch (error) {
            setSendConfirmOpen(false);
            setSendResult({
                success: false,
                message: error.message || 'Failed to send Bundle Builder quote.'
            });
        } finally {
            setSendLoading(false);
        }
    };

    const renderInternet = () => (
        <Grid container spacing={2}>
            {!isAgentBeta && (
                <Grid item xs={12}>
                    <Alert severity={internetConfigState.source === 'active' ? 'success' : 'warning'} variant="outlined">
                        {internetConfigState.source === 'active'
                            ? 'Internet products loaded from active Product Pricing Manager config.'
                            : 'Using fallback Internet config.'}
                    </Alert>
                </Grid>
            )}
            {orderedBySort(activeInternetPlans).map((plan) => {
                const pricing = getInternetPricing(plan, mobilityDiscountActive, builder.eppEnabled, builder.pabIncluded);
                const pabAdjustmentAmount = getInternetPabAdjustmentAmount(plan);
                return (
                    <Grid item xs={12} sm={6} lg={3} key={plan.id}>
                        <ProductCard
                            selected={builder.internetId === plan.id}
                            title={plan.name}
                            subtitle={plan.cardDescription || (plan.notes || []).join(' | ')}
                            price={`${formatMoney(pricing.belowLine)} (${pricing.aboveLine === null ? 'N/A' : formatMoney(pricing.aboveLine)})`}
                            badges={plan.topBadges}
                            chips={plan.chips || ['Below The Line', 'Above The Line in parentheses', builder.pabIncluded ? 'PAB included' : `PAB not included: +${formatMoney(pabAdjustmentAmount)}`]}
                            badgeText={plan.badgeText}
                            badgeColor={plan.badgeColor}
                            recommended={plan.recommended}
                            hot={plan.hot}
                            highlightNote={plan.highlightNote}
                            disabled={plan.quoteEligible === false}
                            onClick={() => updateBuilder({ internetId: builder.internetId === plan.id ? '' : plan.id })}
                        />
                    </Grid>
                );
            })}
            <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                    <Stack spacing={1.5}>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} flexWrap="wrap" useFlexGap>
                            <Chip
                                label={builder.pabIncluded ? 'PAB included' : `PAB not included: +${formatMoney(selectedInternet ? getInternetPabAdjustmentAmount(selectedInternet) : PAB_SURCHARGE)}`}
                                color={builder.pabIncluded ? 'success' : 'warning'}
                                variant="outlined"
                            />
                            <Chip
                                label={mobilityDiscountActive ? 'TELUS/Koodo mobility discount active' : 'No TELUS/Koodo mobility discount'}
                                color={mobilityDiscountActive ? 'success' : 'default'}
                                variant="outlined"
                            />
                            <Chip
                                label={builder.eppEnabled ? 'EPP closing tool active' : 'EPP closing tool off'}
                                color={builder.eppEnabled ? 'success' : 'default'}
                                variant="outlined"
                            />
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                            Use the quick discount controls above to change PAB, mobility discount, and EPP.
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Pre-authorized bank payment discount. Customer must set up auto-pay after install. Service agreement may show the configured PAB discount amount higher before PAB.
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {isAgentBeta ? 'This beta sends Home Services quotes only.' : 'Manage detailed mobility line selection in the Mobility tab.'}
                        </Typography>
                    </Stack>
                </Paper>
            </Grid>
        </Grid>
    );

    const renderTv = () => (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <BundleBuilderTvRecommender
                    onUseBestValue={handleUseTvRecommendation}
                    selectedRecommendationKey={builder.tvRecommendation?.recommendationKey || ''}
                    tvPriceAdjustment={activeTvPriceAdjustment}
                    tvGiftAdjustment={activeTvGiftAdjustment}
                />
            </Grid>
            <Grid item xs={12}>
                <Box display="flex" justifyContent="space-between" alignItems="center" gap={1} flexWrap="wrap">
                    <Box>
                        <Typography variant="h6">Manual TV package selection</Typography>
                        <Typography variant="body2" color="text.secondary">
                            {isAgentBeta
                                ? 'Use a manual package only when a channel-based recommendation is not needed.'
                                : 'Fallback static cards. Prefer the V2 recommender above when building from channel needs.'}
                        </Typography>
                    </Box>
                    {builder.tvRecommendation && (
                        <Chip label="V2 recommendation selected" color="success" />
                    )}
                </Box>
            </Grid>
            {orderedBySort(activeTvPackages).map((plan) => (
                <Grid item xs={12} sm={6} lg={4} key={plan.id}>
                    {(() => {
                        const manualSelected = !builder.tvRecommendation && builder.tvId === plan.id;
                        const recommendationSelected = builder.tvRecommendation?.manualTvId === plan.id;
                        const selected = manualSelected || recommendationSelected;
                        const cardGiftAdjustment = selected ? activeTvGiftAdjustment : null;

                        return (
                            <ProductCard
                                selected={selected}
                                title={plan.name}
                                subtitle={plan.cardDescription || 'First TV box included/free'}
                                price={formatMoney(getTvDisplayPrice(plan, activeTvPriceAdjustment, cardGiftAdjustment))}
                                badges={plan.topBadges}
                                chips={[
                                    recommendationSelected ? 'Matched from V2 recommender' : null,
                                    activeTvPriceAdjustment
                                        ? `${activeTvPriceAdjustment.label} +${formatMoney(activeTvPriceAdjustment.amount)}`
                                        : null,
                                    cardGiftAdjustment
                                        ? `${cardGiftAdjustment.label} +${formatMoney(cardGiftAdjustment.amount)}`
                                        : null,
                                    ...(plan.chips || [])
                                ].filter(Boolean)}
                                badgeText={plan.badgeText}
                                badgeColor={plan.badgeColor}
                                highlightNote={plan.internalAgentNote}
                                onClick={() => updateBuilder(
                                    manualSelected
                                        ? { tvId: '', tvRecommendation: null, extraBoxes: 0, addPvr: false }
                                        : { tvId: plan.id, tvRecommendation: null, addPvr: false }
                                )}
                            />
                        );
                    })()}
                </Grid>
            ))}
            <Grid item xs={12} md={4}>
                <TextField
                    label="Extra TV boxes"
                    type="number"
                    value={builder.extraBoxes}
                    disabled={!selectedTv}
                    onChange={(event) => updateBuilder({ extraBoxes: Math.max(0, Number(event.target.value) || 0) })}
                    fullWidth
                    InputProps={{
                        startAdornment: <InputAdornment position="start">{formatMoney(EXTRA_TV_BOX_PRICE)}/box</InputAdornment>
                    }}
                    inputProps={{ min: 0 }}
                />
            </Grid>
            <Grid item xs={12} md={4}>
                <Stack spacing={0.5}>
                    <Button
                        variant={builder.tvGiftSelected ? 'contained' : 'outlined'}
                        color={builder.tvGiftSelected ? 'success' : 'primary'}
                        disabled={!tvGiftOfferEligibility.eligible}
                        onClick={() => updateBuilder({ tvGiftSelected: !builder.tvGiftSelected })}
                        fullWidth
                        sx={{ minHeight: 56, textTransform: 'none' }}
                    >
                        {TV_GIFT_OFFER.label}
                    </Button>
                    <Typography variant="caption" color={tvGiftOfferEligibility.eligible ? 'text.secondary' : 'warning.main'}>
                        {tvGiftOfferEligibility.reason}
                    </Typography>
                </Stack>
            </Grid>
            <Grid item xs={12} md={4}>
                {selectedTv && selectedTv.pvrMode === 'optional' ? (
                    <FormControlLabel
                        control={<Switch checked={builder.addPvr} onChange={(event) => updateBuilder({ addPvr: event.target.checked })} />}
                        label={`Add PVR +${formatMoney(selectedTv.pvrPrice ?? PVR_PRICE)}`}
                    />
                ) : (
                    <Alert severity="success">
                        {selectedTv
                            ? selectedTv.pvrMode === 'included'
                                ? 'PVR included with selected TV package.'
                                : 'PVR is not included for this TV package.'
                            : 'Select a TV package to manage PVR.'}
                    </Alert>
                )}
            </Grid>
            <Grid item xs={12}>
                <Alert severity="info">
                    {isAgentBeta
                        ? 'All TV orders include free pro install valued at $100. EPP subtracts $5 when TV is selected.'
                        : 'All TV orders include free pro install valued at $100. EPP subtracts $5 when TV is selected. PVR behavior is controlled by Product Pricing Manager TV Display Settings.'}
                </Alert>
            </Grid>
        </Grid>
    );

    const renderPhone = () => (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Alert severity="warning">
                    Home Phone options are for new activations only and must be activated with Internet or Internet + TV.
                </Alert>
            </Grid>
            {!isAgentBeta && (
                <Grid item xs={12}>
                    <Alert severity={homePhoneConfigState.source === 'active' ? 'success' : 'warning'} variant="outlined">
                        {homePhoneConfigState.source === 'active'
                            ? 'Home Phone products loaded from active Product Pricing Manager config.'
                            : 'Using fallback Home Phone config.'}
                    </Alert>
                </Grid>
            )}
            <Grid item xs={12}>
                <Typography variant="h6">Home Phone plans</Typography>
            </Grid>
            {orderedBySort(activePhonePlans).map((plan) => (
                <Grid item xs={12} md={6} key={plan.id}>
                    <ProductCard
                        selected={builder.phoneId === plan.id}
                        title={plan.name}
                        subtitle={plan.cardDescription || `${plan.features}; ${plan.feeNote}; original ${formatMoney(plan.originalPrice)}`}
                        price={formatMoney(plan.price)}
                        badges={plan.topBadges}
                        chips={plan.chips || ['Bundle promo price', 'Required 911 fees included', 'New activations only']}
                        badgeText={plan.badgeText}
                        badgeColor={plan.badgeColor}
                        highlightNote={plan.highlightNote}
                        tone="primary"
                        disabled={plan.quoteEligible === false}
                        onClick={() => updateBuilder(
                            builder.phoneId === plan.id
                                ? { phoneId: '', addonIds: builder.addonIds.filter((addonId) => !activeLongDistanceAddonIds.includes(addonId)) }
                                : { phoneId: plan.id }
                        )}
                    />
                </Grid>
            ))}
            <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'rgba(255, 193, 7, 0.12)', borderColor: 'warning.light' }}>
                    <Typography variant="subtitle1">Home Phone add-ons</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Home Phone add-ons require a Home Phone plan.
                    </Typography>
                </Paper>
            </Grid>
            {orderedBySort(activeLongDistanceAddons).map((addon) => (
                <Grid item xs={12} sm={6} md={4} key={addon.id}>
                    <ProductCard
                        selected={builder.addonIds.includes(addon.id)}
                        title={addon.name}
                        subtitle={addon.cardDescription || addon.note}
                        price={addon.price > 0 ? formatMoney(addon.price) : 'Note'}
                        badges={addon.topBadges}
                        chips={addon.chips || ['Long-distance add-on']}
                        tone="secondary"
                        disabled={!selectedPhone || addon.quoteEligible === false}
                        onClick={() => toggleAddon(addon.id)}
                    />
                </Grid>
            ))}
        </Grid>
    );

    const renderMobility = () => (
        <Grid container spacing={2}>
            {!isAgentBeta && (
                <Grid item xs={12}>
                    <Alert severity={mobilityConfigState.source === 'active' ? 'success' : 'warning'} variant="outlined">
                        {mobilityConfigState.source === 'active'
                            ? 'Mobility products loaded from active Product Pricing Manager config.'
                            : 'Using fallback Mobility config.'}
                    </Alert>
                </Grid>
            )}
            {isAgentBeta && (
                <Grid item xs={12}>
                    <Alert severity="info" variant="outlined">
                        Mobility is separate-flow / quote-only in this beta. It can be included in agent quote totals, but it is excluded from the customer Home Services email.
                    </Alert>
                </Grid>
            )}
            <Grid item xs={12}>
                <Alert severity="info" variant="outlined">
                    Existing TELUS/Koodo mobility customers activate the Internet mobility discount only. They do not appear as a Mobility product line.
                </Alert>
            </Grid>
            {[
                { id: 'none', title: 'No TELUS/Koodo mobility', subtitle: 'No Internet mobility discount.' },
                { id: 'existing', title: 'Existing TELUS/Koodo mobility customer', subtitle: 'Activates Internet mobility discount without adding a new line.' },
                { id: 'adding', title: 'Adding new Mobility line', subtitle: 'Activates Internet mobility discount and adds selected line pricing.' }
            ].map((state) => (
                <Grid item xs={12} md={4} key={state.id}>
                    <ProductCard
                        selected={builder.mobilityState === state.id}
                        title={state.title}
                        subtitle={state.subtitle}
                        chips={state.id === 'none' ? ['No discount'] : ['Mobility discount', state.id === 'adding' && isAgentBeta ? 'Separate flow' : null].filter(Boolean)}
                        badgeText={state.id === 'adding' ? 'Add Lines' : undefined}
                        badgeColor="primary"
                        onClick={() => updateBuilder({ mobilityState: state.id, mobilityId: state.id === 'adding' ? builder.mobilityId : '' })}
                    />
                </Grid>
            ))}

            {builder.mobilityState === 'adding' && (
                <>
                    <Grid item xs={12}>
                        <Alert severity="info">
                            Do not combine Personal, Business, and EPP/RBC plans in one mobility quote. Choose one provider/category for this draft quote.
                            Mobility prices include PAB by default. Turning PAB off adds $10/line where applicable.
                        </Alert>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            label="Mobility lines"
                            type="number"
                            value={builder.mobilityLines}
                            onChange={(event) => updateBuilder({ mobilityLines: Math.max(1, Number(event.target.value) || 1) })}
                            fullWidth
                            inputProps={{ min: 1 }}
                        />
                    </Grid>
                    <Grid item xs={12} md={8}>
                        <Paper variant="outlined" sx={{ px: 1 }}>
                            <Tabs
                                value={mobilityCategory}
                                onChange={(event, value) => {
                                    setMobilityCategory(value);
                                    if (selectedMobility && selectedMobility.category !== value) {
                                        updateBuilder({ mobilityId: '' });
                                    }
                                }}
                                variant="scrollable"
                                scrollButtons="auto"
                            >
                                {activeMobilityCategories.map((category) => (
                                    <Tab key={category.id} value={category.id} label={category.label} />
                                ))}
                            </Tabs>
                        </Paper>
                    </Grid>

                    {filteredMobilityPlans.map((plan) => {
                        const total = getMobilityTotal(plan, builder.mobilityLines, builder.pabIncluded);
                        const linePricingNote = plan.linePricingNote || formatMobilityLinePricingNote(plan.linePricing);
                        const planChips = plan.chips?.length ? plan.chips : [
                            plan.type,
                            linePricingNote || (plan.complexPricing ? 'Complex line pricing' : `${formatMoney(plan.price)}/line base`),
                            builder.pabIncluded ? 'PAB included' : 'PAB not included: +$10/line',
                            isAgentBeta ? 'Separate flow' : null
                        ].filter(Boolean);
                        return (
                            <Grid item xs={12} md={6} key={plan.id}>
                                <ProductCard
                                    selected={builder.mobilityId === plan.id}
                                    title={plan.name}
                                    subtitle={plan.note || linePricingNote}
                                    price={formatMoney(total)}
                                    badges={plan.topBadges}
                                    chips={planChips}
                                    badgeText={plan.badgeText}
                                    badgeColor={plan.badgeColor}
                                    hot={plan.hot}
                                    onClick={() => updateBuilder({ mobilityId: builder.mobilityId === plan.id ? '' : plan.id })}
                                />
                            </Grid>
                        );
                    })}
                </>
            )}

            {builder.mobilityState !== 'adding' && (
                <Grid item xs={12}>
                    <Alert severity="info">
                        New mobility line pricing appears only when "Adding new Mobility line" is selected. Existing mobility only activates the Internet discount.
                    </Alert>
                </Grid>
            )}
        </Grid>
    );

    const renderSecurity = () => (
        <Grid container spacing={2}>
            {!isAgentBeta && (
                <Grid item xs={12}>
                    <Alert severity={securityConfigState.source === 'active' ? 'success' : 'warning'} variant="outlined">
                        {securityConfigState.source === 'active'
                            ? 'Security products loaded from active Product Pricing Manager config.'
                            : 'Using fallback Security config.'}
                    </Alert>
                </Grid>
            )}
            {isAgentBeta && (
                <Grid item xs={12}>
                    <Alert severity="info" variant="outlined">
                        Security is separate-flow / quote-only in this beta. It can be included in agent quote totals, but it is excluded from the customer Home Services email.
                    </Alert>
                </Grid>
            )}
            {orderedBySort(activeSecurityPlans).map((plan) => (
                <Grid item xs={12} md={6} key={plan.id}>
                    <ProductCard
                        selected={builder.securityId === plan.id}
                        title={plan.name}
                        subtitle={plan.devices}
                        price={formatMoney(plan.price)}
                        badges={plan.topBadges}
                        chips={[
                            ...(plan.chips?.length ? plan.chips : [plan.term, 'Free installation']),
                            isAgentBeta ? 'Separate flow' : null
                        ].filter(Boolean)}
                        badgeText={plan.badgeText}
                        badgeColor={plan.badgeColor}
                        onClick={() => updateBuilder({ securityId: builder.securityId === plan.id ? '' : plan.id })}
                    />
                </Grid>
            ))}
            <Grid item xs={12}>
                <Alert severity="info">
                    Security has a 3-year contract and installation is separate from internet installation.
                </Alert>
            </Grid>
        </Grid>
    );

    const renderAddons = () => (
        <Stack spacing={2}>
            {!isAgentBeta && (
                <Alert severity={addonsConfigState.source === 'active' ? 'success' : 'warning'} variant="outlined">
                    {addonsConfigState.source === 'active'
                        ? 'Add-ons loaded from active Product Pricing Manager config.'
                        : 'Using fallback Add-ons config.'}
                </Alert>
            )}
            {isAgentBeta && (
                <Alert severity="info" variant="outlined">
                    Add-ons are quote-only in this beta. They can be included in agent quote totals, but they are excluded from the customer Home Services email.
                </Alert>
            )}
            {orderedBySort(activeAddonGroups).map((group) => (
                <Accordion key={group.id} disableGutters>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                            <Typography variant="subtitle1">{group.title}</Typography>
                            <Chip label={`${group.items.length} options`} size="small" variant="outlined" />
                            {group.helperText && (
                                <Typography variant="caption" color="text.secondary">
                                    {group.helperText}
                                </Typography>
                            )}
                        </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Grid container spacing={2}>
                            {orderedBySort(group.items).map((addon) => {
                                const selected = builder.addonIds.includes(addon.id);
                                const quantity = builder.addonQuantities[addon.id] || 1;
                                return (
                                    <Grid item xs={12} sm={6} md={4} key={addon.id}>
                                        <ProductCard
                                            selected={selected}
                                            title={addon.name}
                                            subtitle={addon.note}
                                            price={formatMoney(addon.price)}
                                            badges={addon.topBadges}
                                            chips={[
                                                ...(addon.chips?.length ? addon.chips : [
                                                    addon.allowQuantity ? 'Quantity available' : group.title,
                                                    addon.price === 0 ? '$0' : 'Monthly'
                                                ]),
                                                isAgentBeta ? 'Quote-only' : null
                                            ].filter(Boolean)}
                                            badgeText={addon.badgeText}
                                            badgeColor={addon.badgeColor}
                                            onClick={() => toggleAddon(addon.id)}
                                        >
                                            {addon.allowQuantity && selected && (
                                                <Box
                                                    onClick={(event) => event.stopPropagation()}
                                                    onMouseDown={(event) => event.stopPropagation()}
                                                >
                                                    <TextField
                                                        label="Quantity"
                                                        type="number"
                                                        size="small"
                                                        value={quantity}
                                                        onChange={(event) => updateAddonQuantity(addon.id, event.target.value)}
                                                        fullWidth
                                                        inputProps={{ min: 1 }}
                                                    />
                                                </Box>
                                            )}
                                        </ProductCard>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    </AccordionDetails>
                </Accordion>
            ))}
            <Accordion disableGutters>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle1">Sales notes</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                    <Stack spacing={1.25}>
                        <Typography variant="subtitle1">Bill credit and sales notes</Typography>
                        <Typography variant="body2" color="text.secondary">
                            $50 one-time bill credit applies only when Internet is selected, mobility discount is active, and TV or Home Phone is selected.
                            Internet alone and Internet + Mobility only do not receive a bill credit.
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Shaw cancellation fee notes, final bill credit eligibility, and sales notes should be verified before quoting.
                        </Typography>
                        <Divider />
                        <Typography variant="subtitle2">Seasonal pricing notes</Typography>
                        {seasonalNotes.map((note) => (
                            <Typography key={note} variant="body2" color="text.secondary">
                                {note}
                            </Typography>
                        ))}
                        <Divider />
                        <Typography variant="subtitle2">Install time slot notes</Typography>
                        {installNotes.map((note) => (
                            <Typography key={note} variant="body2" color="text.secondary">
                                {note}
                            </Typography>
                        ))}
                    </Stack>
                    </Paper>
                </AccordionDetails>
            </Accordion>
        </Stack>
    );

    const builderTabs = [
        { key: 'internet', label: 'Internet', icon: <InternetIcon />, render: renderInternet },
        { key: 'tv', label: 'TV', icon: <TvIcon />, render: renderTv },
        { key: 'home-phone', label: 'Home Phone', icon: <HomeIcon />, render: renderPhone },
        { key: 'mobility', label: 'Mobility', icon: <MobilityIcon />, render: renderMobility },
        { key: 'security', label: 'Security', icon: <SecurityIcon />, render: renderSecurity },
        { key: 'addons', label: 'Add-ons', icon: null, render: renderAddons }
    ];
    const activeBuilderTab = Math.min(activeTab, builderTabs.length - 1);
    const quoteSectionOrder = ['Internet', 'TV', 'Home Phone', 'New Mobility Line', 'Smart Home Security', 'Add-ons'];
    const quoteSectionLabels = isAgentBeta ? {
        'New Mobility Line': 'Mobility - Separate flow',
        'Smart Home Security': 'Security - Separate flow',
        'Add-ons': 'Add-ons - Quote-only'
    } : {};
    const quoteItemsBySection = quoteSectionOrder
        .map((section) => ({
            section,
            items: quote.items.filter((item) => item.section === section)
                .filter((item) => !(item.section === 'TV' && item.label === 'PVR' && item.amount === 0))
        }))
        .filter((group) => group.items.length > 0);
    const savingsStyle = quote.savings ? {
        saves: {
            label: `Customer saves ${formatMoney(quote.savings.monthlyDifference)}/mo`,
            color: 'success',
            bgcolor: 'rgba(46, 125, 50, 0.08)',
            borderColor: 'success.light'
        },
        similar: {
            label: 'Similar monthly price',
            color: 'primary',
            bgcolor: 'rgba(25, 118, 210, 0.08)',
            borderColor: 'primary.light'
        },
        more: {
            label: `Customer pays ${formatMoney(Math.abs(quote.savings.monthlyDifference))}/mo more`,
            color: 'warning',
            bgcolor: 'rgba(237, 108, 2, 0.08)',
            borderColor: 'warning.light'
        }
    }[quote.savings.status] : null;

    const renderInternetPriceBreakdown = () => {
        if (!quote.internetPriceBreakdown) {
            return null;
        }

        const { rows, finalPrice } = quote.internetPriceBreakdown;
        const shouldShowFinalRow = rows.some((row) => row.amount < 0 || row.amount !== finalPrice);

        return (
            <Paper variant="outlined" sx={{ p: 1.25, mt: 0.5, mb: 1, bgcolor: 'rgba(25, 118, 210, 0.04)' }}>
                <Stack spacing={0.75}>
                    <Typography variant="subtitle2">Internet price breakdown</Typography>
                    {rows.map((row) => (
                        <Box key={row.label} display="flex" justifyContent="space-between" gap={2}>
                            <Typography variant="body2" color="text.secondary">{row.label}</Typography>
                            <Typography variant="body2" fontWeight={700}>{formatSignedMoney(row.amount)}</Typography>
                        </Box>
                    ))}
                    {shouldShowFinalRow && (
                        <>
                            <Divider />
                            <Box display="flex" justifyContent="space-between" gap={2}>
                                <Typography variant="body2" fontWeight={700}>Final Internet price</Typography>
                                <Typography variant="body2" fontWeight={800} color="primary.main">
                                    {formatMoney(finalPrice)}
                                </Typography>
                            </Box>
                        </>
                    )}
                </Stack>
            </Paper>
        );
    };

    const renderTvPriceBreakdown = () => {
        if (!quote.tvPriceBreakdown) {
            return null;
        }

        return (
            <Paper variant="outlined" sx={{ p: 1.25, mt: 0.5, mb: 1, bgcolor: 'rgba(25, 118, 210, 0.04)' }}>
                <Stack spacing={0.75}>
                    <Typography variant="subtitle2">TV price breakdown</Typography>
                    <Box display="flex" justifyContent="space-between" gap={2}>
                        <Typography variant="body2" color="text.secondary">Base TV price</Typography>
                        <Typography variant="body2" fontWeight={700}>{formatMoney(quote.tvPriceBreakdown.basePrice)}</Typography>
                    </Box>
                    {quote.tvPriceBreakdown.adjustmentAmount > 0 && (
                        <Box display="flex" justifyContent="space-between" gap={2}>
                            <Typography variant="body2" color="text.secondary">
                                {quote.tvPriceBreakdown.adjustmentLabel || 'Internet TV adjustment'}
                            </Typography>
                            <Typography variant="body2" fontWeight={700}>
                                +{formatMoney(quote.tvPriceBreakdown.adjustmentAmount)}
                            </Typography>
                        </Box>
                    )}
                    {quote.tvPriceBreakdown.giftAdjustmentAmount > 0 && (
                        <Box display="flex" justifyContent="space-between" gap={2}>
                            <Typography variant="body2" color="text.secondary">
                                {quote.tvPriceBreakdown.giftAdjustmentLabel || TV_GIFT_OFFER.label}
                            </Typography>
                            <Typography variant="body2" fontWeight={700}>
                                +{formatMoney(quote.tvPriceBreakdown.giftAdjustmentAmount)}
                            </Typography>
                        </Box>
                    )}
                    <Box display="flex" justifyContent="space-between" gap={2}>
                        <Typography variant="body2" color="text.secondary">EPP discount</Typography>
                        <Typography variant="body2" fontWeight={700}>
                            {quote.tvPriceBreakdown.eppDiscount > 0 ? `-${formatMoney(quote.tvPriceBreakdown.eppDiscount)}` : 'Not applied'}
                        </Typography>
                    </Box>
                    <Divider />
                    <Box display="flex" justifyContent="space-between" gap={2}>
                        <Typography variant="body2" fontWeight={700}>Final TV price</Typography>
                        <Typography variant="body2" fontWeight={800} color="primary.main">
                            {formatMoney(quote.tvPriceBreakdown.finalTotal)}
                        </Typography>
                    </Box>
                </Stack>
            </Paper>
        );
    };

    return (
        <Box>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} mb={3}>
                <Box>
                    <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                        <QuoteIcon color="primary" />
                        <Typography variant="h4">{isAgentBeta ? 'Bundle Builder Beta' : 'Bundle Builder Admin Tools'}</Typography>
                        <Chip label={isAgentBeta ? 'Live Beta' : 'Admin Tools'} color={isAgentBeta ? 'primary' : 'warning'} size="small" />
                    </Stack>
                    <Typography variant="body1" color="text.secondary">
                        {isAgentBeta
                            ? 'Build Home Services quotes, add quote-only Mobility, Security, and Add-ons when needed, then send the approved Home Services customer email.'
                            : 'Admin-only preview and testing workspace for Bundle Builder utilities.'}
                    </Typography>
                </Box>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                    {canOpenAdminTools && (
                        <Button
                            component={RouterLink}
                            to="/coming-soon/bundle-builder"
                            variant="outlined"
                            size="small"
                        >
                            Open Admin Tools
                        </Button>
                    )}
                    <Button variant="outlined" startIcon={<ResetIcon />} onClick={() => setBuilder(initialBuilderState)}>
                        Reset Builder
                    </Button>
                </Stack>
            </Box>

            <Alert severity="info" sx={{ mb: 3 }}>
                {isAgentBeta
                    ? 'Beta: Home Services email send. Mobility, Security, and Add-ons are quote-only / separate-flow for now.'
                    : 'Admin tools only. The live agent-facing Bundle Builder Beta is available from Offers & Email.'}
            </Alert>

            <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={5}>
                        <TextField
                            label="Customer current monthly bill"
                            value={builder.currentMonthlyBill}
                            onChange={(event) => {
                                const { value } = event.target;
                                if (value === '' || /^\d*\.?\d*$/.test(value)) {
                                    updateBuilder({ currentMonthlyBill: value });
                                }
                            }}
                            type="number"
                            size="small"
                            fullWidth
                            InputProps={{
                                startAdornment: <InputAdornment position="start">$</InputAdornment>
                            }}
                            inputProps={{ min: 0, step: 1 }}
                            helperText="Optional. Leave blank if unknown."
                        />
                    </Grid>
                    <Grid item xs={12} md={7}>
                        <Typography variant="body2" color="text.secondary">
                            Compare the customer&apos;s current monthly bill against this TELUS bundle subtotal. This does not change product pricing.
                        </Typography>
                    </Grid>
                </Grid>
            </Paper>

            {renderDiscountToggleBar()}

            <Grid container spacing={3}>
                <Grid item xs={12} xl={8}>
                    <BuilderCategoryTabs activeTab={activeBuilderTab} onChange={setActiveTab} tabs={builderTabs} />
                    {builderTabs[activeBuilderTab].render()}
                </Grid>

                <QuoteSummary
                    builder={builder}
                    bundleBuilderSendSummary={bundleBuilderSendSummary}
                    copyStatus={copyStatus}
                    isAgentBeta={isAgentBeta}
                    mobilityDiscountActive={mobilityDiscountActive}
                    offerEmailPreview={offerEmailPreview}
                    onCopyQuote={copyQuote}
                    onPrepareSend={() => {
                        setPreviewCopyStatus('');
                        setSendDryRunOpen(isAgentBeta);
                        setSendDryRunResult(null);
                        setSendValidationErrors({});
                        setSendResult(null);
                        setOfferPreviewOpen(true);
                    }}
                    onReset={() => setBuilder(initialBuilderState)}
                    quote={quote}
                    quoteItemsBySection={quoteItemsBySection}
                    quoteSectionLabels={quoteSectionLabels}
                    renderInternetPriceBreakdown={renderInternetPriceBreakdown}
                    renderTvPriceBreakdown={renderTvPriceBreakdown}
                    savingsStyle={savingsStyle}
                    selectedTv={selectedTv}
                />
            </Grid>

            <BundleBuilderSendDialogs
                bundleBuilderSendSummary={bundleBuilderSendSummary}
                canShowFinalSendPreview={canShowFinalSendPreview}
                copyOfferEmailHtml={copyOfferEmailHtml}
                copyOfferEmailText={copyOfferEmailText}
                currentUser={currentUser}
                finalEmailPreviewHtml={finalEmailPreviewHtml}
                handleBundleAttachmentUpload={handleBundleAttachmentUpload}
                handlePrepareSendDryRun={handlePrepareSendDryRun}
                handleSendBundleOffer={handleSendBundleOffer}
                isAgentBeta={isAgentBeta}
                offerEmailPreview={offerEmailPreview}
                offerPreviewOpen={offerPreviewOpen}
                previewCopyStatus={previewCopyStatus}
                quote={quote}
                removeBundleAttachment={removeBundleAttachment}
                resetSendValidation={resetSendValidation}
                sendAttachments={sendAttachments}
                sendConfirmOpen={sendConfirmOpen}
                sendDryRunData={sendDryRunData}
                sendDryRunLoading={sendDryRunLoading}
                sendDryRunOpen={sendDryRunOpen}
                sendDryRunResult={sendDryRunResult}
                sendLoading={sendLoading}
                sendResult={sendResult}
                sendValidationErrors={sendValidationErrors}
                setOfferPreviewOpen={setOfferPreviewOpen}
                setSendConfirmOpen={setSendConfirmOpen}
                setSendDryRunData={setSendDryRunData}
                setSendDryRunOpen={setSendDryRunOpen}
                setSendDryRunResult={setSendDryRunResult}
                setSendResult={setSendResult}
                setSendValidationErrors={setSendValidationErrors}
            />
        </Box>
    );
}

export default BundleBuilderPrototype;
